import { Component, Inject, inject, input, Input, Optional, SimpleChanges } from '@angular/core';
import { commonImports } from '../shared/table-interface';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { codeableConceptDataType, CodeableConceptFieldFromBackEnd, formMetaData, generalFieldsData } from '../shared/dynamic-forms.interface2';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { CodeableConceptField, CodeField, codingDataType, GroupField, IndividualField, IndividualReferenceField, ReferenceFieldArray, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { MatSelectModule } from '@angular/material/select';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { forkJoin, map, Observable, of, startWith } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Bundle, CodeableConcept, Coding, Observation } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
// import { FormFields } from '../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { UtilityService } from '../shared/utility.service';
import { formFields } from '../shared/dynamic-forms.interface';
import { units } from 'fhirclient/lib/lib';
import { ErrorService } from '../shared/error.service';
import { StateService } from '../shared/state.service';
import { AuthService } from '../shared/auth/auth.service';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { PatSympService } from './pat-symp.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ResourceRefreshService } from '../shared/resource-refresh.service';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;


@Component({
  selector: 'app-pat-symp',
  imports: [...commonImports,
    MatSelectModule, MatDatepickerModule,
    MatStepperModule, MatSlideToggleModule, DynamicFormsV2Component,
    CommonModule, MatTooltipModule, FormsModule, MatAutocompleteModule,
    EmphasisDirective,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './pat-symp.component.html',
  styleUrls: ['../encounter-check/encounter-check.component.scss', './pat-symp.component.scss']
})
export class PatSympComponent {
  @Input() forClerkingSheet = false;
  @Input() hideSubmitAction = false;
  patSympService = inject(PatSympService);
  private resourceRefresh = inject(ResourceRefreshService);
  private readonly resourceStatusChanges = inject(ResourceStatusChangesService);

  fb = inject(FormBuilder);
  @Input() editMode: boolean = false;

  dialog = inject(MatDialog);
  dialogRef = inject(MatDialogRef<any>, { optional: true });
  // authService = inject(AuthService);

  get isPatient(): boolean {
    const user = this.authService.currentUserValue;
    return user?.accountType === 'Patient' || user?.role?.toLowerCase() === 'patient';
  }

  symptomsFormMetaData: formMetaData = {
    formName: '',
    formDescription: '',
    showSubmitButton: true,
    submitText: 'Add Symptom',
  };
  riskFactorsFormMetaData: formMetaData = {
    formName: '',
    formDescription: '',
    showSubmitButton: true,
    submitText: 'Add Risk Factor',
  };


  /**
   * Single “source of truth” form that aggregates both symptoms + risk factors.
   * UI sections can patch into this form on valueChanges.
   */
  sourceObservationForm = this.fb.group({
    observations: this.fb.array<FormGroup>([]),
  });
  backendApiBaseUrl: any = inject(backendEndPointToken)

  /** Dialog data (when opened via MatDialog). */
  readonly data = inject(MAT_DIALOG_DATA, { optional: true });



  patientPresentedSymptomsFromForm: {
    category: {
      "system": "http://terminology.hl7.org/CodeSystem/observation-category",

      "code": "social-history",
      "display": "Social History"
    } | {
      "system": "http://terminology.hl7.org/CodeSystem/observation-category",

      "code": "exam",
      "display": "Exam"
    },
    code: Coding,
    valueString?: string,
    valueCodeableConcept?: {
      code: 'mild-severity',
      system: string,
      display: 'Mild'
    } | {
      code: 'moderate-severity',
      system: string,
      display: 'Moderate'
    } | {
      code: 'severe-severity',
      system: string,
      display: 'Severe'
    },
    status: 'preliminary' | 'final' | 'amended' | 'corrected',
    effectiveDateTime: string,
    effectivePeriod?: {
      start: string,
      end: string
    }
  }[] = [];
  symptomsSeverityFormConfig: FormFields[] = [
    <SingleCodeField>{
      generalProperties: {
        fieldApiName: 'valueCodeableConcept',
        fieldName: 'Symptom Severity',
        fieldLabel: 'Symptom Severity',
        fieldType: 'CodeableConceptField',
        isArray: false,
        isGroup: false
      },
      data: [{
        code: 'mild-severity',
        system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
        display: 'Mild'
      }, {
        code: 'moderate-severity',
        system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
        display: 'Moderate'
      }, {
        code: 'severe-severity',
        system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
        display: 'Severe'
      }]
    }
  ]
  symptomDurationFormConfig: FormFields[] = [
    <GroupField>{
      generalProperties: {
        fieldApiName: 'effectivePeriod',
        fieldLabel: 'Symptom Duration',
        groupFieldsHint: `Provide the duration for which the symptom has been experienced, 
        If the symptom is ongoing, leave the end field blank`,
        fieldName: 'Symptom Duration',
        fieldType: 'IndividualField',

        isArray: false,
        isGroup: true
      },
      groupFields: {
        'start': <IndividualField>{
          generalProperties: {
            fieldApiName: 'start',
            fieldName: 'Start Date',
            fieldLabel: 'Start Date',
            fieldType: 'IndividualField',
            isArray: false,
            inputType: 'datetime-local',
            isGroup: false,
            // inputType: 'date'
          },
        },
        'end': <IndividualField>{
          generalProperties: {
            fieldApiName: 'end',
            fieldName: 'End Date',
            fieldLabel: 'End Date',
            // fieldHint: 'If the symptom is ongoing, leave this field blank',
            fieldType: 'IndividualField',
            isArray: false,
            isGroup: false,
            inputType: 'datetime-local',
            // inputType: 'date'
          },
        }
      },
      keys: ['start', 'end']

    }
  ]


  formFieldsDataService = inject(FormFieldsSelectDataService);
  utilityService = inject(UtilityService);


  observationCategoryForm = new FormControl(null);
  @Input() initialData?: Observation[];

  constructor() {
    if (this.data) {
      const editMode = this.data.editMode || false;
      this.editMode = this.editMode || editMode;
      if (this.data.initialData) {

      }
    } else {

    }

    this.observationCategoryForm.valueChanges.subscribe(e => {
      console.log(e);
    });



  }

  editSymptomFieldThroughDialog(values: Observation) {
    //set values of each field from the initial data
    // if (!this.initialData ) return;


    const refinedFormFields: FormFields[] = [];
    //determine categeory

    const category = values.category?.[0] && values.category[0].coding && values.category[0].coding.length > 0
      ? values.category[0].coding[0].code
      : '';
    if (category === 'social-history') {
    } else if (category === 'exam') {
      //build fields for symptoms
      this.consolidateSymptomsFields();
      //set values for fields

      const formFieldsDataForDialog = this.getValuesForSymptomFieldsThroughInitialData(values, this.allFieldsForSymptoms);
      //now call dialog
      const dialogRef = this.dialog.open(DynamicFormsV2Component, {
        maxWidth: '650px',
        maxHeight: '90vh',
        data: {
          formFields: formFieldsDataForDialog,
          formMetaData: {
            formName: 'Edit Symptom',
            formDescription: 'Edit the details of the symptom',
            showSubmitButton: true,
            closeDialogOnSubmit: true,
            submitText: 'Save Changes'
          }
        }
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        console.log('Dialog closed with result:', result);
        if (result) {
          //set returned values to initial data
          const editedData = this.setReturnedSymptomValuesToInitialData(this.allFieldsForSymptoms, result.values, values);
          console.log('Edited Symptom Data:', editedData);
        }



      })
    }

  }

  private getCategoryCodeFromCategory(category: any): 'exam' | 'social-history' | '' {
    const code = category?.[0]?.coding?.[0]?.code;
    return code === 'exam' || code === 'social-history' ? code : '';
  }

  getCategoryCodeFromGroup(group: FormGroup): 'exam' | 'social-history' | '' {
    return this.getCategoryCodeFromCategory(group.get('category')?.value);
  }

  getCategoryLabelFromGroup(group: FormGroup): string {
    const categoryCode = this.getCategoryCodeFromGroup(group);
    return categoryCode === 'exam' ? 'Symptom' : categoryCode === 'social-history' ? 'Social / Risk Factor' : 'Item';
  }

  getDurationFromEffectivePeriodFromGroup(group: FormGroup): string {
    const p = group.get('effectivePeriod')?.value as { start?: string; end?: string } | null;
    return this.patSympService.getDurationFromEffectivePeriod({
      start: p?.start,
      end: p?.end
    });
  }
  isSeverityValue(value: string): boolean {
    if (!value) return false;
    const normalized = value.toLowerCase().trim();
    return ['mild', 'moderate', 'severe', 'mild-severity', 'moderate-severity', 'severe-severity'].includes(normalized);
  }

  getIsChiefComplaintFromGroup(ctrl: FormGroup): boolean {

    const hasChiefComplaint = (category: any): boolean =>
      Array.isArray(category) &&
      category.some((cc: any) => Array.isArray(cc?.coding) && cc.coding.some((c: any) => c?.code === 'chief-complaint'));

    // Check the in-progress (not-yet-added) item
    if (hasChiefComplaint(ctrl?.get('category')?.value)) return true;
    else return false;

    // Check already-added items in the shared FormArray
    // const controls = this.fields?.controls ?? [];
    // return controls.some(ctrl => hasChiefComplaint((ctrl as FormGroup).get('category')?.value));
  }


  getCategoryIconFromGroup(group: FormGroup): string {
    const categoryCode = this.getCategoryCodeFromGroup(group);
    return categoryCode === 'exam' ? 'healing' : categoryCode === 'social-history' ? 'people' : 'notes';
  }

  private extractFirstCoding(value: any): Coding | undefined {
    // Handles either CodeableConcept {coding:[...]}, or Coding directly, or weirdly shaped data.
    if (!value) return undefined;
    if (typeof value === 'string') return undefined;
    if (value.code && value.system) return value as Coding;

    const coding = value?.coding;
    if (Array.isArray(coding) && coding.length > 0) {
      const c0 = coding[0];
      if (c0?.code || c0?.display) return c0 as Coding;
    }
    if (coding && (coding.code || coding.display)) return coding as Coding;
    return undefined;
  }

  private getCodeDisplayFromAny(codeValue: any): string {
    if (!codeValue) return '';
    if (typeof codeValue === 'string') return codeValue;

    const coding = this.extractFirstCoding(codeValue);
    if (coding?.display) return coding.display;
    if (coding?.code) return coding.code;
    if (codeValue?.text) return codeValue.text;
    return '';
  }

  getCodeDisplayFromGroup(group: FormGroup): string {
    return this.getCodeDisplayFromAny(group.get('code')?.value);
  }

  private extractValueCodeableConceptCoding(valueCodeableConcept: any): Coding | undefined {
    // Stored shape in this component can be CodeableConcept, or CodeableConcept[], or even {coding: Coding}
    if (!valueCodeableConcept) return undefined;
    if (Array.isArray(valueCodeableConcept)) {
      const first = valueCodeableConcept[0];
      return this.extractFirstCoding(first);
    }
    return this.extractFirstCoding(valueCodeableConcept);
  }

  getValueSummaryFromGroup(group: FormGroup): string {
    const valueQuantity = group.get('valueQuantity')?.value;
    const valueString = group.get('valueString')?.value;
    const valueBoolean = group.get('valueBoolean')?.value;
    const valueCodeableConcept = group.get('valueCodeableConcept')?.value;

    if (valueString) return String(valueString);
    if (valueQuantity?.value !== undefined && valueQuantity?.value !== null) {
      return `${valueQuantity.value}${valueQuantity.unit ? ' ' + valueQuantity.unit : ''}`;
    }
    if (valueBoolean === true) return 'Yes';
    if (valueBoolean === false) return 'No';
    const coding = this.extractValueCodeableConceptCoding(valueCodeableConcept);
    if (coding?.display) return coding.display;
    if (coding?.code) return coding.code;
    return '';
  }

  getEffectivePeriodDisplayFromGroup(group: FormGroup): any {
    const p = group.get('effectivePeriod')?.value as { start?: string; end?: string } | null;
    // if (!p?.start) return '—';
    // return p.end ? `${p.start} → ${p.end}` : `${p.start} → ongoing`;
    return {
      start: p?.start ?? undefined,
      end: p?.end ?? undefined
    }
  }

  private buildObservationDraftFromDynamicFormValues(submitted: any, category: any): any {
// $#$    // /* alert('Building draft from submitted values: ' + JSON.stringify(submitted) */);
    const draft: any = { category };

    if (submitted?.hasOwnProperty('name')) {
      draft.code = submitted.name?.code
        ? <CodeableConcept>{
          coding: [
            {
              system: submitted.name.system,
              code: submitted.name.code,
              display: submitted.name.display,
            },
          ],
        }
        : typeof submitted.name === 'string' && submitted.name.split('$#$').length < 2 && submitted.name
          ? <CodeableConcept>{ text: submitted.name }
          : typeof submitted.name === 'string' && submitted.name
            ? <CodeableConcept>{
              coding: [
                {
                  system: submitted.name.split('$#$')[2],
                  code: submitted.name.split('$#$')[0],
                  display: submitted.name.split('$#$')[1],
                },
              ],
            }
            : undefined;
    }

    if (submitted?.hasOwnProperty('status')) {
      draft.status = submitted.status;
    }

    if (submitted?.hasOwnProperty('chiefComplaint')) {
      const isChiefComplaint = submitted.chiefComplaint === true;
      if (isChiefComplaint) {
        if (Array.isArray(draft.category) && draft.category.length > 0) {
          const hasChiefComplaint = draft.category.some((cat: CodeableConcept) =>
            cat.coding?.some(coding => coding.code === 'chief-complaint')
          );
          if (!hasChiefComplaint) {
            draft.category.push({
              coding: [
                {

                  system: `${this.backendApiBaseUrl}/CodeSystem/observation-category-codes`,
                  code: 'chief-complaint',
                  display: 'Chief Complaint',
                },
              ],
            });
          }
        }
      } else {
        // Remove chief-complaint coding if present.
        if (Array.isArray(draft.category) && draft.category.length > 0) {
          draft.category = draft.category.filter((cat: CodeableConcept) =>
            !(cat.coding?.some(coding => coding.code === 'chief-complaint'))
          );

        }
      }
    }

    if (submitted?.hasOwnProperty('effectivePeriod') && submitted.effectivePeriod?.start) {
      draft.effectivePeriod = {
        start: submitted.effectivePeriod.start,
        ...(submitted.effectivePeriod.end ? { end: submitted.effectivePeriod.end } : {}),
      };
    }

    if (submitted?.hasOwnProperty('valueCodeableConcept')) {
      // Always clear value[x] first to prevent lingering values when edits change the type.
      this.clearObservationValueX(draft);
      if (submitted.valueCodeableConcept) {
        // Keep same persisted shape as existing submitFromDynamicForm logic
        draft.valueCodeableConcept = <CodeableConcept>{ coding: [submitted.valueCodeableConcept] };
      }
    }

    if (submitted?.hasOwnProperty('riskFactorDetails')) {
      const riskFactorDetails = submitted.riskFactorDetails;

      // Clear any previous value[x] before setting the one matching the chosen type.
      // This avoids stale values from previous edits.
      this.clearObservationValueX(draft);

      // Only set the value[x] that matches the chosen details type.
      // This prevents stale/extra subfields (e.g. from previous edits) from setting multiple value[x]s.
      const resultType: string | undefined = riskFactorDetails?.result_type;
      switch (resultType) {
        case 'Number': {
          // Exception: result_value + result_unit belong together.
          if (
            riskFactorDetails?.hasOwnProperty('result_value') &&
            riskFactorDetails.result_value !== null &&
            riskFactorDetails.result_value !== undefined
          ) {
            draft.valueQuantity = {
              value: riskFactorDetails.result_value,
              unit: riskFactorDetails.result_unit ? riskFactorDetails.result_unit : undefined,
            };
          }
          break;
        }

        case 'Text': {
// $#$          // /* alert('Setting Text Value' + riskFactorDetails.result_value_text); */
          if (riskFactorDetails?.hasOwnProperty('result_value_text') && riskFactorDetails.result_value_text) {
            draft.valueString = riskFactorDetails.result_value_text;
          }
          break;
        }

        case 'True or False': {
          if (
            riskFactorDetails?.hasOwnProperty('result_boolean') &&
            (typeof riskFactorDetails.result_boolean === 'boolean' ||
              (typeof riskFactorDetails.result_boolean === 'string' &&
                (riskFactorDetails.result_boolean === 'Yes' || riskFactorDetails.result_boolean === 'No')))
          ) {
            draft.valueBoolean = riskFactorDetails.result_boolean === true || riskFactorDetails.result_boolean === 'Yes';
          }
          break;
        }

        case 'Severity Level': {
          if (riskFactorDetails?.hasOwnProperty('result_codeableConcept') && riskFactorDetails.result_codeableConcept) {
            draft.valueCodeableConcept = <CodeableConcept>{ coding: [riskFactorDetails.result_codeableConcept] };
          }
          break;
        }

        default:
          // If no type selected, don't set any value[x] from riskFactorDetails.
          break;
      }
    }

    // Default status
    if (!draft.status) draft.status = 'final';
    if (!draft.effectivePeriod) draft.effectivePeriod = {};
    return draft;
  }

  private syncValueTypeControls(group: FormGroup, draft: any): void {
    const valueKeys = ['valueString', 'valueQuantity', 'valueBoolean', 'valueCodeableConcept'] as const;
    for (const key of valueKeys) {
      const has = Object.prototype.hasOwnProperty.call(draft, key);
      if (has) {
        if (!group.contains(key)) {
          group.addControl(key, new FormControl(draft[key]));
        } else {
          group.get(key)?.setValue(draft[key]);
        }
      } else if (group.contains(key)) {
        group.removeControl(key);
      }
    }
  }

  private applyDraftToGroup(group: FormGroup, draft: any): void {
    group.patchValue(
      {
        category: draft.category ?? [],
        code: draft.code ?? '',
        status: draft.status ?? 'final',
        ...(draft.effectivePeriod ? { effectivePeriod: draft.effectivePeriod } : {}),
      },
      { emitEvent: true }
    );
    this.syncValueTypeControls(group, draft);
  }

  private getValuesForRiskFactorFieldsThroughInitialData(values: any, fields: FormFields[]): FormFields[] {
    const name: Coding | string | undefined =
      values?.code?.coding && Array.isArray(values.code.coding) && values.code.coding.length > 0
        ? values.code.coding[0]
        : values?.code?.text ?? (typeof values?.code === 'string' ? values.code : undefined);

    const status: string | undefined = values?.status;
    const valueQuantity = values?.valueQuantity;
    const valueString = values?.valueString;
    const valueBoolean = values?.valueBoolean;
    const valueCodeableConceptCoding = this.extractValueCodeableConceptCoding(values?.valueCodeableConcept);
    const chiefComplaint: boolean = (<CodeableConcept[]>values?.category)?.some(cat =>
      cat.coding?.some(coding => coding.code === 'chief-complaint')
    );
    fields.forEach(f => {
      const fieldApiName = f.generalProperties.fieldApiName;
      switch (fieldApiName) {
        case 'name':
          f.generalProperties['value'] = name;
          break;
        case 'status':
          f.generalProperties['value'] = status;
          break;
        case 'riskFactorDetails': {
          const g = f as GroupField;
          // Determine which "details type" should show (mirrors buildObservationDraftFromDynamicFormValues).
          // Clear all to avoid stale values in the dialog.
          g.groupFields['result_value'].generalProperties['value'] = undefined;
          g.groupFields['result_unit'].generalProperties['value'] = undefined;
          g.groupFields['result_value_text'].generalProperties['value'] = undefined;
          g.groupFields['result_boolean'].generalProperties['value'] = undefined;
          g.groupFields['result_codeableConcept'].generalProperties['value'] = undefined;

          const resultType: string | undefined =
            valueQuantity?.value !== undefined && valueQuantity?.value !== null
              ? 'Number'
              : valueString
                ? 'Text'
                : valueBoolean === true || valueBoolean === false
                  ? 'True or False'
                  : valueCodeableConceptCoding
                    ? 'Severity Level'
                    : undefined;

          g.groupFields['result_type'].generalProperties['value'] = resultType;

          switch (resultType) {
            case 'Number': {
              g.groupFields['result_value'].generalProperties['value'] = valueQuantity.value;
              g.groupFields['result_unit'].generalProperties['value'] = valueQuantity.unit;
              break;
            }

            case 'Text': {
              g.groupFields['result_value_text'].generalProperties['value'] = valueString;
              break;
            }

            case 'True or False': {
              g.groupFields['result_boolean'].generalProperties['value'] = valueBoolean ? 'Yes' : 'No';
              break;
            }

            case 'Severity Level': {
              g.groupFields['result_codeableConcept'].generalProperties['value'] = valueCodeableConceptCoding;
              break;
            }

            default:
              // No details present; keep everything cleared.
              break;
          }
          break;
        }
          break;
        case 'chiefComplaint':
          f.generalProperties['value'] = chiefComplaint;
      }


    });

    return fields;
  }

  editObservationAtIndex(index: number): void {
    if (index < 0 || index >= this.fields.length) return;
    const group = this.fields.at(index) as FormGroup;
    const current = group.getRawValue() as any;
    const categoryCode = this.getCategoryCodeFromCategory(current?.category);

    if (categoryCode === 'exam') {
      this.consolidateSymptomsFields();
      const dialogFields = this.getValuesForSymptomFieldsThroughInitialData(this.observationForEachArrayElement[index], this.cloneFields(this.allFieldsForSymptoms));
      // console.log('Dialog Fields for Symptom Edit:', dialogFields.map((f: any) => ({ fieldApiName: f.generalProperties.fieldApiName, value: f.generalProperties.value })));
      const dialogRef = this.dialog.open(DynamicFormsV2Component, {
        maxWidth: '650px',
        maxHeight: '90vh',
        data: {
          formFields: dialogFields,
          formMetaData: {
            formName: 'Edit Symptom',
            formDescription: 'Edit the symptom details',
            showSubmitButton: true,
            closeDialogOnSubmit: true,
            submitText: 'Save Changes',
          },
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (!result?.values) return;
        const draft = this.buildObservationDraftFromDynamicFormValues(result.values, current.category);
        this.applyDraftToGroup(group, draft);
        this.observationForEachArrayElement[index] = { ...this.observationForEachArrayElement[index], ...draft };
// $#$        // /* alert('Updated Symptom Observation: ' + JSON.stringify(draft) */);
      });
      return;
    }

    if (categoryCode === 'social-history') {
      this.consolidateRiskFactorsFields();
      // const dialogFields = this.getValuesForRiskFactorFieldsThroughInitialData(current, this.cloneFields(this.allFieldsForRiskFactors));
      const dialogFields = this.getValuesForRiskFactorFieldsThroughInitialData(this.observationForEachArrayElement[index], this.cloneFields(this.allFieldsForRiskFactors));
      const dialogRef = this.dialog.open(DynamicFormsV2Component, {
        maxWidth: '650px',
        maxHeight: '90vh',
        data: {
          formFields: dialogFields,
          formMetaData: {
            formName: 'Edit Social / Risk Factor',
            formDescription: 'Edit the factor and its details',
            showSubmitButton: true,
            closeDialogOnSubmit: true,
            submitText: 'Save Changes',
          },
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (!result?.values) return;
        const draft = this.buildObservationDraftFromDynamicFormValues(result.values, current.category);
        this.applyDraftToGroup(group, draft);
        this.observationForEachArrayElement[index] = draft;
      });
      return;
    }

    this.errorService.openandCloseError('Unable to edit: unknown category on this entry.');
  }
  getValuesForSymptomFieldsThroughInitialData(values: Observation, fields: FormFields[]): any {
    console.log('Initial Symptom Values:', values);
    const name: Coding | string | undefined =
      values?.code?.coding && values.code.coding.length > 0
        ? values.code.coding[0]
        : values?.code?.text;
    const status: string | undefined = values?.status;
    const chiefComplaint: boolean = (<CodeableConcept[]>values?.category)?.some(cat =>
      cat.coding?.some(coding => coding.code === 'chief-complaint')
    );
    const effectivePeriod: { start?: string; end?: string } | undefined = values?.effectivePeriod
    const severity: Coding | undefined = values?.valueCodeableConcept?.coding && values.valueCodeableConcept.coding.length > 0
      ? values.valueCodeableConcept.coding[0]
      : undefined;
    fields.forEach(f => {
      const fieldApiName = f.generalProperties.fieldApiName;
      switch (fieldApiName) {
        case 'name':
          f.generalProperties['value'] = name;
          break;
        case 'status':
          f.generalProperties['value'] = status;
          break;
        case 'effectivePeriod':
          (f as GroupField).groupFields['start'].generalProperties['value'] = effectivePeriod?.start;
          (f as GroupField).groupFields['end'].generalProperties['value'] = effectivePeriod?.end;
          break;
        case 'valueCodeableConcept':
          f.generalProperties['value'] = severity
// $#$          // /* alert('Severity Value Set in Field: ' + JSON.stringify(severity) */);
          break;
        case 'chiefComplaint':
          f.generalProperties['value'] = chiefComplaint;
      }
    });
    console.log('Symptom Fields with Values:', fields);

    return fields;
  }

  setReturnedSymptomValuesToInitialData(fieldGroup: FormFields[], returnedValues: any, initialValues?: Observation) {
    if (!initialValues) return;
    const name = returnedValues['name'];
    const status = returnedValues['status'];
    const effectivePeriod = returnedValues['effectivePeriod'];
// $#$    // /* alert(JSON.stringify(effectivePeriod) */);
// $#$    // /* alert(JSON.stringify(returnedValues) */);
    const valueCodeableConcept = returnedValues['valueCodeableConcept'];
    initialValues.code = typeof name === 'string' ? { text: name } : {
      coding: [{
        system: name.system,
        code: name.code,
        display: name.display
      }]
    };
    initialValues.status = status;
    initialValues.effectivePeriod = effectivePeriod;
    initialValues.valueCodeableConcept = valueCodeableConcept;
    return initialValues;
  }


  allFieldsForRiskFactors: FormFields[] = [];

  allFieldsForSymptoms: FormFields[] = [];

  private cloneValue<T>(value: T): T {
    const sc = (globalThis as any).structuredClone as ((v: any) => any) | undefined;
    if (typeof sc === 'function') return sc(value) as T;
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private cloneFields(fields: FormFields[]): FormFields[] {
    return fields.map(f => this.cloneValue(f));
  }

  consolidateSymptomsFields() {
    if (this.observationformFields.length === 0) return;
    const defaultFields = this.cloneFields(
      this.observationformFields.filter(f => {
        // if (
        //   this.editMode &&
        //   this.initialData?.status.trim().toLowerCase() === 'final' &&
        //   f.generalProperties.fieldApiName === 'status'
        // ) {
        //   return false;
        // }
        return true;
      })
    );
    defaultFields.forEach(f => {
      if (f.generalProperties.fieldApiName === 'name') {
        f.generalProperties.fieldLabel = 'Symptom Name';
        f.generalProperties.fieldName = 'Symptom Name';
        f.generalProperties.fieldHint = 'Enter, search and pick from suggested names or enter your own';
      }
    });
    const symptomsSeverityField = this.symptomsSeverityFormConfig[0];
    const symptomDurationField = this.symptomDurationFormConfig[0];
    this.allFieldsForSymptoms = [
      ...defaultFields,
      symptomsSeverityField,
      symptomDurationField
    ];
  }

  consolidateRiskFactorsFields() {
    if (this.observationformFields.length === 0) return;
    const defaultFields = this.cloneFields(
      this.observationformFields.filter(f => {
        // if (
        //   this.editMode &&
        //   this.initialData?.status.trim().toLowerCase() === 'final' &&
        //   f.generalProperties.fieldApiName === 'status'
        // ) {
        //   return false;
        // }
        return true;
      })
    );
    defaultFields.forEach(f => {
      if (f.generalProperties.fieldApiName === 'name') {
        f.generalProperties.fieldLabel = 'Social / Familial Factor';
        f.generalProperties.fieldName = 'Social / Familial Factor';
        f.generalProperties.fieldHint = 'Enter, search and pick from suggested social, familial or environmental factors or enter your own';
      }
    });

    this.allFieldsForRiskFactors = [
      ...defaultFields,
      ...this.riskFactorsValueFormConfig[1].formFields
    ];
  }
  riskFactorMaskedLabelMap = {
    'name': 'Social / Familial Factor',
    'value': 'Details',

  }
  symptomsMaskedLabelMap = {
    'name': 'Symptom Name',
    'value': 'Symptom Severity',
    'effectivePeriod': 'Symptom Duration',
  }



  /** Convenience accessor for the shared feeds array. */
  get fields(): FormArray<FormGroup> {
    return this.sourceObservationForm.get('observations') as FormArray<FormGroup>;
  }

  /**
   * Creates a new feed item that can represent either a Symptom or a Risk Factor.
   * Keep this schema broad; your downstream transform can map it to Observation.
   */
  private buildFieldGroup(values: Observation): FormGroup {
    return this.fb.group({


      // Observation.category (we keep as a single coding for simplicity)
      category: [values.category ? values.category : []],

      // Observation.code.coding[0]
      code: [values.code ? values.code : ''],

      // Observation.status
      status: [values.status || 'final'],

      // Timing – either single date/time or a period (you already model this)

      effectivePeriod: [values.effectivePeriod ? values.effectivePeriod : {}],

      ...(values.valueCodeableConcept ? { valueCodeableConcept: values.valueCodeableConcept } : {}),
      ...(values.valueString ? { valueString: values.valueString } : {}),
      ...(values.valueQuantity ? { valueQuantity: values.valueQuantity } : {}),
      ...(values.valueBoolean !== undefined ? { valueBoolean: values.valueBoolean } : {}),

      // Generic “answer” bucket. Different UI sections can fill different fields.

    });
  }

  changeStatusAtIndex(index: number): void {
    if (index < 0 || index >= this.fields.length) return;
    const observation = this.observationForEachArrayElement[index];
    const observationId = observation?.id || '';
    if (!observationId) {
      this.errorService.openandCloseError('This symptom has no id yet; submit it first before changing status.');
      return;
    }

    const currentStatus = observation?.status || '';
    const availableStatuses = this.resourceStatusChanges.getStatusCodes('Observation') || [
      'registered',
      'preliminary',
      'final',
      'amended',
      'corrected',
      'cancelled',
      'entered-in-error',
      'unknown'
    ];

    const dynF = this.dialog.open(DynamicFormsV2Component, {
      width: '420px',
      data: {
        formMetaData: <formMetaData>{
          formName: 'Change Symptom Status',
          closeDialogOnSubmit: true,
          formDescription: currentStatus ? `Current status: ${currentStatus}` : 'Select a new status',
          submitText: 'Update Status'
        },
        formFields: [
          <SingleCodeField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'status',
              fieldLabel: 'Status',
              fieldName: 'Status',
              fieldType: 'SingleCodeField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              value: currentStatus,
              validations: [{ type: 'default', isFunction: false, name: 'required' }],
              auth: { read: 'all', write: 'doctor, nurse, admin' }
            },
            data: availableStatuses,
            dataType: 'string'
          }
        ]
      }
    });

    dynF.afterClosed().subscribe((formResult) => {
      const newStatus = formResult?.values?.status;
      if (!newStatus || newStatus === currentStatus) return;

      this.resourceStatusChanges
        .changeResourceStatus('Observation', observationId, newStatus, this.stateService.currentPatientId$.getValue() ? 'patient' : 'auto')
        .subscribe({
          next: (resp) => {
            const updatedResource = (resp as any)?.response;
            if (updatedResource) {
              this.observationForEachArrayElement[index] = updatedResource;
            } else {
              this.observationForEachArrayElement[index] = { ...observation, status: newStatus } as any;
            }
            const group = this.fields.at(index) as FormGroup;
            group.patchValue({ status: newStatus });
          }
        });
    });
  }

  /**
   * Add a new item to the source FormArray.
   * Use this from Symptoms UI or Risk Factors UI.
   */
  addField(values: Observation): FormGroup {
    const group = this.buildFieldGroup(values);
    this.fields.push(group);
    this.observationForEachArrayElement.push(values);
    return group;

  }



  /** Remove an item from the source FormArray by index. */
  removeField(index: number): void {
    if (index < 0 || index >= this.fields.length) return;
    this.fields.removeAt(index);
  }
  riskFactorsValueFormConfig: [string, { formFields: FormFields[] }] = // Define category appropriately or remove if not needed
    ['value', {
      formFields: [
        <GroupField>{
          groupFields: {
            'result_type': <SingleCodeField>{
              generalProperties: {
                fieldApiName: 'result_type',
                fieldName: 'Details Type',
                fieldLabel: 'Type of Details',
                controllingField: [{
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'True or False',
                  controlledFieldDependencyId: "result_type.trueorfalse"
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Number',
                  controlledFieldDependencyId: "result_type.number2"
                },
                {
                  isAControlField: true,
                  controlledFieldDependencyId: "result_type.number",
                  dependentFieldVisibilityTriggerValue: 'Number'
                },
                {
                  isAControlField: true,
                  controlledFieldDependencyId: "result_type.text",
                  dependentFieldVisibilityTriggerValue: 'Text'
                },
                {
                  isAControlField: true,
                  controlledFieldDependencyId: "result_type.coding",
                  dependentFieldVisibilityTriggerValue: 'Severity Level'
                }
                ],
                fieldType: 'SingleCodeField',
                isArray: false,
                isGroup: false
              },
              data: ['Number', 'Text', 'True or False', 'Severity Level']
            },
            'result_value': <IndividualField>{
              generalProperties: {
                fieldApiName: 'result_value',
                fieldName: 'Result Value',
                fieldLabel: 'Result Value',
                inputType: 'number',
                dependence_id: 'result_type.number',
                fieldType: 'IndividualField',
                isArray: false,
                isGroup: false
              },
            },
            'result_value_text': <IndividualField>{
              generalProperties: {
                fieldApiName: 'result_value_text',
                fieldName: 'Details in text',
                fieldLabel: 'Details in text',
                inputType: 'textarea',
                dependence_id: 'result_type.text',
                fieldType: 'IndividualField',
                isArray: false,
                isGroup: false
              },
            },
            'result_unit': <IndividualField>{
              generalProperties: {
                fieldApiName: 'result_unit',
                fieldName: 'Unit',
                fieldLabel: 'Unit',
                fieldHint: 'Pick from suggested units or enter your own',
                dependence_id: 'result_type.number2',
                fieldType: 'SingleCodeField',
                isArray: false,
                isGroup: false
              },

              data: this.utilityService.observation_units
            },
            'result_boolean': <IndividualField>{
              generalProperties: {
                fieldApiName: 'result_boolean',
                fieldName: 'Yes or No Details',
                fieldLabel: 'Yes or No Details',
                dependence_id: 'result_type.trueorfalse',
                fieldType: 'SingleCodeField',

                isArray: false,
                isGroup: false,

              },
              data: ['Yes', 'No']
            },
            'result_codeableConcept': <SingleCodeField>{
              generalProperties: {
                fieldApiName: 'result_codeableConcept',
                fieldName: 'Severity Level Details',
                fieldLabel: 'Severity Level Details',
                dependence_id: 'result_type.coding',
                fieldType: 'CodeableConceptField',
                isArray: false,
                isGroup: false
              },
              data: [{
                code: 'mild-severity',
                system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
                display: 'Mild'
              }, {
                code: 'moderate-severity',
                system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
                display: 'Moderate'
              }, {
                code: 'severe-severity',
                system: `${this.backendApiBaseUrl}/CodeSystem/symptom-severity`,
                display: 'Severe'
              }]
            },



          },

          keys: ['result_type', 'result_value', 'result_value_text', 'result_unit',
            'result_boolean', 'result_codeableConcept'
          ],
          generalProperties: {
            fieldApiName: 'riskFactorDetails',
            fieldName: 'Social / Familial Factor Details',
            fieldLabel: 'Social / Familial Factor Details',
            groupFieldsHint: 'Provide details for the selected social or familial risk factor',
            fieldType: 'SingleCodeField',
            isArray: false,
            isGroup: true
          },
        },
      ]
    }];

  currentValues: any = {};

  private clearObservationValueX(target: any): void {
    if (!target) return;
    delete target.valueString;
    delete target.valueQuantity;
    delete target.valueBoolean;
    delete target.valueCodeableConcept;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData']) {
      const newInitialData = (changes['initialData'].currentValue ?? []) as Observation[];
      //prvent duplicates
      this.fields.clear();
      this.observationForEachArrayElement = [];

      if (Array.isArray(newInitialData)) {
        newInitialData.forEach((element: Observation) => {
          this.addField(element);
        });
      }
    }
  }

  ngOnInit() {

    //if edit mode and
    if (this.initialData) {
      this.fields.clear();
      this.observationForEachArrayElement = [];
      this.initialData.forEach((element: Observation) => {
        this.addField(element);
      })
    }

    // Initialize riskFactorsValueFormConfig after utilityService is available
    // this.riskFactorsValueFormConfig = this.buildRiskFactorsValueFormConfig();

    forkJoin({
      practitioner: this.formFieldsDataService.getFormFieldSelectData('observation', 'practitioner'),
      //  category: this.formFieldsDataService.getFormFieldSelectData('observation', category),
      code: this.formFieldsDataService.getFormFieldSelectData('observation', 'ontoServerCode'),
    }).subscribe((g: any) => {
      const observationformFieldsMap: [string, { formFields: FormFields[] }][] = [

        ['status',
          {

            formFields: [
              <SingleCodeField>{
                generalProperties: {
                  fieldApiName: 'status',
                  fieldName: 'Observation Status',
                  fieldLabel: 'Observation Status',
                  fieldType: 'SingleCodeField',
                  isArray: false,
                  isGroup: false,
                  isHidden: true,
                  value: 'final',
                  validations: [{ type: 'default', name: 'required' }]
                },
                data: ['preliminary', 'final']
              }
            ] as FormFields[]
          }],
        ['cheif-complaint',
          {
            formFields: [
              <IndividualField>{
                generalProperties: {
                  fieldApiName: 'chiefComplaint',
                  fieldName: 'Is Chief Complaint',
                  fieldLabel: 'Is Chief Complaint',
                  fieldType: 'IndividualField',
                  inputType: 'toggle',
                  isArray: false,
                  isGroup: false
                },
              }
            ] as FormFields[]
          }


        ],
        ['name', {
          formFields:
            <FormFields[]>[
              {
                generalProperties: {
                  fieldApiName: 'name',
                  fieldName: 'Name of Observation / Test',
                  fieldHint: 'Pick from suggested names or enter your own',
                  fieldLabel: 'Name of Observation / Test',
                  allowedOthers: true,
                  allowTextForCodeableConcept: true,
                  fieldType: 'CodeableConceptFieldFromBackEnd',
                  isArray: false,
                  validations: [{ type: 'default', name: 'required' }],
                  isGroup: false
                },
                data: g.code
              },
            ]
        }],

      ]
      if (this.editMode && this.data?.initialData) {
        observationformFieldsMap.unshift()
      }

      this.observationformFields = [...observationformFieldsMap.map(f => (f[1].formFields as FormFields[])[0])]//.flat();
      this.consolidateSymptomsFields();
      this.consolidateRiskFactorsFields();

    })


  }
  observationformFields: FormFields[] = [];
  setCategory(e: any) {
    if (e === 'social-history') {
      this.currentValues.category = <CodeableConcept[]>[{
        coding: [{
          "system": "http://terminology.hl7.org/CodeSystem/observation-category",
          "code": "social-history",
          "display": "Social History"
        }]
      }]
    }
    if (e === 'exam') {
      this.currentValues.category = <CodeableConcept[]>[{
        coding: [{
          "system": "http://terminology.hl7.org/CodeSystem/observation-category",
          "code": "exam",
          "display": "Exam"
        }]
      }]
    }
  }

  setOrUnsetChiefComplaintCategory(e: boolean) {
    // Uses the second element of Observation.category[] to represent "chief-complaint".
    const category = this.currentValues?.category;
    if (!Array.isArray(category) || category.length === 0) return;

    const chiefComplaint: CodeableConcept = {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/observation-category',
          code: 'chief-complaint',
          display: 'Chief Complaint'
        }
      ]
    };

    const isChiefComplaint = (cc: any): boolean =>
      Array.isArray(cc?.coding) && cc.coding.some((c: any) => c?.code === 'chief-complaint');

    if (e) {
      // Set/replace index 1 with chief-complaint (idempotent).
      if (!category[1] || !isChiefComplaint(category[1])) {
        category[1] = chiefComplaint;
      }
      return;
    }

    // Unset chief-complaint only if it's actually at index 1.
    if (category[1] && isChiefComplaint(category[1])) {
      category.splice(1, 1);
    }
    this.currentValues.category = category;

  }

  observationForEachArrayElement: Observation[] = [];
  submitFromDynamicForm(e: any, dy: DynamicFormsV2Component) {
// $#$    // /* alert('hey' + JSON.stringify(dy.aForm.value) */);
    dy.aForm.patchValue({ status: e.status });
    console.log(e);
    if (e.hasOwnProperty('name')) {

      this.currentValues.code = e.name.code ? <CodeableConcept>{
        coding: [{
          system: e.name.system,
          code: e.name.code,
          display: e.name.display
        }]
      } : e.name.split('$#$').length < 2 && e.name ? <CodeableConcept>{
        text: e.name
      } : e.name ? <CodeableConcept>{
        coding: [{
          system: e.name.split('$#$')[2],
          code: e.name.split('$#$')[0],
          display: e.name.split('$#$')[1]
        }]
      } : undefined;
    }

    if (e.hasOwnProperty('status')) {
      this.currentValues.status = e.status;
    }


    if (e.hasOwnProperty('effectivePeriod') && e.effectivePeriod.start


    ) {
// $#$      // /* alert(JSON.stringify(e.effectivePeriod) */);
      this.currentValues.effectivePeriod = {
        start: e.effectivePeriod.start,
        ...(e.effectivePeriod.end ? { end: e.effectivePeriod.end } : {})
      }
// $#$      // /* alert(JSON.stringify(this.currentValues.effectivePeriod) */);
    }

    //different value types
    if (e.hasOwnProperty('valueCodeableConcept')) {
      // Ensure only one value[x] exists at a time and avoid lingering values.
      this.clearObservationValueX(this.currentValues);
      if (e.valueCodeableConcept) {
// $#$        // /* alert(e.valueCodeableConcept); */
        this.currentValues.valueCodeableConcept = <CodeableConcept>{ coding: [e.valueCodeableConcept] };
      }
    }

    if (e.hasOwnProperty('riskFactorDetails')) {
      const riskFactorDetails = e.riskFactorDetails;

      // Only set the matching value[x] based on the selected type.
      // Always clear first to avoid lingering values when user switches types.
      this.clearObservationValueX(this.currentValues);

      const resultType: string | undefined = riskFactorDetails?.result_type;
      switch (resultType) {
        case 'Number': {
          // result_value + result_unit belong together.
          if (
            riskFactorDetails?.hasOwnProperty('result_value') &&
            riskFactorDetails.result_value !== null &&
            riskFactorDetails.result_value !== undefined
          ) {
            this.currentValues.valueQuantity = {
              value: riskFactorDetails.result_value,
              unit: riskFactorDetails.result_unit ? riskFactorDetails.result_unit : undefined
            }
          }
          break;
        }

        case 'Text': {
          if (riskFactorDetails?.hasOwnProperty('result_value_text') && riskFactorDetails.result_value_text) {
            this.currentValues.valueString = riskFactorDetails.result_value_text;
          }
          break;
        }

        case 'True or False': {
          if (
            riskFactorDetails?.hasOwnProperty('result_boolean') &&
            ((riskFactorDetails.result_boolean !== null &&
              riskFactorDetails.result_boolean !== undefined && typeof riskFactorDetails.result_boolean === 'boolean') ||
              (typeof riskFactorDetails.result_boolean === 'string' &&
                (riskFactorDetails.result_boolean === 'Yes' || riskFactorDetails.result_boolean === 'No'))
            )
          ) {
            this.currentValues.valueBoolean = riskFactorDetails.result_boolean === true || riskFactorDetails.result_boolean === 'Yes';
          }
          break;
        }

        case 'Severity Level': {
          if (riskFactorDetails?.hasOwnProperty('result_codeableConcept') && riskFactorDetails.result_codeableConcept) {
            this.currentValues.valueCodeableConcept = <CodeableConcept>{ coding: [riskFactorDetails.result_codeableConcept] };
          }
          break;
        }

        default:
          // No type selected or unknown type => no value[x] should exist.
          break;
      }

      // isAControlField: true,
      //               dependentFieldVisibilityTriggerValue: 'True or False',
      //               controlledFieldDependencyId: "result_type.trueorfalse"
      //             },
      //             {
      //               isAControlField: true,
      //               dependentFieldVisibilityTriggerValue: 'Number',
      //               controlledFieldDependencyId: "result_type.number2"
      //             },
      //             {
      //               isAControlField: true,
      //               controlledFieldDependencyId: "result_type.number",
      //               dependentFieldVisibilityTriggerValue: 'Number'
      //             },
      //             {
      //               isAControlField: true,
      //               controlledFieldDependencyId: "result_type.text",
      //               dependentFieldVisibilityTriggerValue: 'Text'
      //             },
      //             {
      //               isAControlField: true,
      //               controlledFieldDependencyId: "result_type.coding",
      //               dependentFieldVisibilityTriggerValue: 'Severity Level'
      //             }


    }
    //chiefComplaint

    if (e.hasOwnProperty('chiefComplaint')) {
      this.setOrUnsetChiefComplaintCategory(e.chiefComplaint);
    }
    if (!this.validateCurrentValues()) {
      this.addField(this.currentValues);
      const lastCategoryCode = this.currentValues.category && this.currentValues.category.length > 0 &&
        this.currentValues.category[0].coding && this.currentValues.category[0].coding.length > 0 ?
        this.currentValues.category[0].coding[0].code : '';

      //reset current values
      this.currentValues = {};
      dy.aForm.reset();
      // this.setCategory(lastCategoryCode);

    } else {
      this.errorService.openandCloseError('Please fix the errors before submitting the form.' + this.currentErrorForCurrentValues);
    }
  }
  errorService = inject(ErrorService);
  currentErrorForCurrentValues: string = '';
  validateCurrentValues(): string {

    this.currentErrorForCurrentValues = '';
    let categoryFromCurrentValues: CodeableConcept[] | undefined = this.currentValues.category && this.currentValues.category.length > 0 ? this.currentValues.category : undefined;
    if (!categoryFromCurrentValues) {
      this.currentErrorForCurrentValues += '---Internal Error - Category wasn\'t set'
      return this.currentErrorForCurrentValues;
    };
    let categoryToUse = categoryFromCurrentValues && categoryFromCurrentValues[0].coding && categoryFromCurrentValues[0].coding.length > 0 ? categoryFromCurrentValues[0].coding[0].code : '';
    if (categoryToUse !== 'social-history' && categoryToUse !== 'exam') {
      this.currentErrorForCurrentValues += '---Internal Error - Category is invalid'
      return this.currentErrorForCurrentValues;
    }
    if (this.editMode && this.data?.initialData && this.data.initialData.status.trim().toLowerCase() === 'final') {
      this.currentValues.status = 'amended';
    }
    //if still no status, set to final
    if (!this.currentValues.status) {
      this.currentValues.status = 'final';
    }
    if (!this.currentValues.code) {
// $#$      // /* alert('Please provide a valid Observation/Test name.'); */
      let errMssg = categoryToUse && categoryToUse === 'social-history' ? '---Please, provide the actual social, enviromental or health factor' :
        '---Please, provide the actual symptom';

      this.currentErrorForCurrentValues += errMssg;

    }
    //check all possible values
    if (!this.currentValues.valueString && !this.currentValues.valueCodeableConcept && !this.currentValues.valueQuantity
      && !this.currentValues.valueBoolean
    ) {
// $#$      // /* alert('Please provide a value for the Observation/Test.'); */
      let errMssg2 = categoryToUse && categoryToUse === 'social-history' ? '---Please, provide the details or findings related to the social, enviromental or health factor' :
        '---Please, provide the symptom severity';
      this.currentErrorForCurrentValues += errMssg2;
    }
    //no category
    if (!this.currentValues.category) {
      let errMssg3 = '---Internal Error - Category wasn\'t set'
      this.currentErrorForCurrentValues += errMssg3

    }
// $#$    // /* alert(this.currentErrorForCurrentValues); */
    return this.currentErrorForCurrentValues;

  }
  stateService = inject(StateService);
  authService = inject(AuthService);
  fhirService = inject(FhirResourceService);
  addSystemGeneratedValuesAndSubmit() {
    // Iterate through each observation and add system-generated values
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [] as any[]
    }
    for (let i = 0; i < this.observationForEachArrayElement.length; i++) {
      const observation: any = this.observationForEachArrayElement[i];
      //add category of patientsymptoms if not present
      observation.category!.push({
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/observation-category',
          code: 'patient-symptoms',
          display: 'Patient Symptoms'
        }]
      });





      // Add system-generated values (subject, performer, issued date, encounter)
      observation.subject = {
        reference: `Patient/${this.stateService.currentPatientId$.getValue()}`, // Replace with actual patient reference

      };
      observation.performer = [{
        reference: `Patient/${this.stateService.currentPatientId$.getValue()}`, // Replace with actual patient reference
      }];

      observation.issued = new Date().toISOString(); // Current date and time
      observation.encounter = {
        reference: `Encounter/${this.stateService.currentEncounter.getValue()?.id}` // Replace with actual encounter reference
      };
      observation.resourceType = 'Observation'

      const method = (this.editMode ? 'PUT' : 'POST') as 'PUT' | 'POST';
      if (method === 'PUT' && !observation?.id) {
        this.errorService.openandCloseError('Unable to update: one or more observations has no id, so a PUT cannot be performed.');
        return;
      }

      const obsevationEntry = {
        resource: observation,
        request: {
          method,
          url: method === 'PUT' ? `Observation/${observation.id}` : 'Observation'
        }
      };
      bundle.entry!.push(obsevationEntry);

    }
    // Submit the bundle to the FHIR server
    this.fhirService.postBundle(bundle).subscribe({
      next: (response) => {
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: 'All Observations submitted successfully!' },
          duration: 3000
        });
        try { this.resourceRefresh.notify('patient-observation', { full: true }); } catch (e) { }
        this.dialogRef?.close(true);
// $#$        // /* alert('All Observations submitted successfully!'); */
        // You can add more logic here, like navigating away or resetting the form
      },
      error: (error) => {
        this.errorService.openandCloseError('Error submitting observations: ' + JSON.stringify(error));
      }
    });
    // You can add more system-generated values as needed
  }
  snackBar = inject(MatSnackBar);

  get canUpdateAll(): boolean {
    if (!this.editMode) return true; // "Submit All" mode
    const role = (this.authService.user?.getValue?.()?.role || this.authService.user?.getValue?.()?.['role'] || '').toString().toLowerCase();
    // Only Clinical staff can "Update All" (Edit existing observations)
    return ['doctor', 'remotedoctor', 'healthcareassistant'].includes(role);
  }


  // You can add more system-generated values as needed
}

